import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

/**
 * Mirrors the backend's {@code BookingPolicyResponse} record. Both
 * fields are returned together so the frontend can pick the right one
 * based on the customer's VIP status (sourced from {@code useVipStatus}).
 *
 * <p>The VIP window is tenant-scoped (same for any sport at this
 * tenant). The regular max-advance is sport-keyed — the endpoint
 * mirrors {@code AvailabilityService.java:86}'s resolution path so the
 * calendar popover never offers a date the backend would reject at
 * booking time.
 */
export type BookingPolicy = {
  maxAdvanceBookingDaysRegular: number;
  vipBookingWindowDays: number;
};

/**
 * Resolves the booking horizon for a given (location, sport) pair.
 * Sport-keyed because per-sport overrides at the location are real
 * (see {@code location.sportConfigOverrides} on the backend).
 *
 * <p>Cached 5 minutes — policy rarely changes, no need for fresher.
 * `enabled` short-circuits when either id is missing (e.g. during the
 * venue-by-slug resolution).
 */
export function useBookingPolicy(locationId: string | undefined, sportCode: string | undefined) {
  return useQuery({
    queryKey: ['bookingPolicy', locationId, sportCode],
    queryFn: () =>
      apiClient<BookingPolicy>(
        `/locations/${locationId}/booking-policy?sportCode=${encodeURIComponent(sportCode ?? '')}`,
        { skipAuth: true },
      ),
    enabled: !!locationId && !!sportCode,
    staleTime: 5 * 60 * 1000,
  });
}
